import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api, mediaUrl } from '@/lib/api';
import { ChannelPageHero, ChannelEmpty } from '@/pages/WatchPage';
import { motion } from 'framer-motion';
import { ArrowLeft } from 'lucide-react';
import { sanitizeHtml } from '@/lib/sanitize';

function renderBody(text) {
  if (!text) return null;
  const trimmed = String(text).trim();
  // Detect TipTap HTML output (starts with a block tag). Render sanitized.
  if (/^<(p|h[1-6]|ul|ol|blockquote|pre|hr|div)(\s|>)/i.test(trimmed)) {
    return <div dangerouslySetInnerHTML={{ __html: sanitizeHtml(trimmed) }} />;
  }
  // Legacy markdown-ish plain-text fallback.
  const blocks = text.split(/\n{2,}/);
  return blocks.map((b, i) => {
    const t = b.trim();
    if (t.startsWith('### ')) return <h3 key={i}>{t.slice(4)}</h3>;
    if (t.startsWith('## ')) return <h2 key={i}>{t.slice(3)}</h2>;
    if (t.startsWith('# ')) return <h2 key={i}>{t.slice(2)}</h2>;
    if (t.startsWith('> ')) return <blockquote key={i}>{t.slice(2)}</blockquote>;
    return <p key={i}>{t}</p>;
  });
}

export function ReadList() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/articles').then(({ data }) => { setItems(data); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  const featured = items.find(a => a.featured) || items[0];
  const rest = items.filter(a => a.id !== featured?.id);

  return (
    <div>
      <ChannelPageHero kicker="CHANNEL 03" title="READ" blurb="Thoughts, articles, observations, and daily drops. Stay as long as you like." accent="gold" />

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 pb-20">
        {loading ? (
          <div className="pg-card p-10 text-center text-[var(--pg-ink-muted)]">Tuning in…</div>
        ) : items.length === 0 ? (
          <ChannelEmpty title="THE PEN IS ON THE DESK" copy="Essays and observations will land here. Subscribe to read them when they drop." />
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            {featured && (
              <Link to={`/read/${featured.slug}`} className="lg:col-span-7 pg-card pg-card-hover relative overflow-hidden block">
                {featured.cover_url && <div className="h-64 sm:h-80 bg-cover bg-center" style={{ backgroundImage: `url('${mediaUrl(featured.cover_url)}')` }} />}
                <div className="p-6 sm:p-8">
                  <p className="pg-text-glow-gold font-mono text-[10px] tracking-[0.32em]">FEATURED</p>
                  <h2 className="font-serif text-3xl sm:text-4xl mt-3 text-[var(--pg-ink)] leading-tight">{featured.title}</h2>
                  {featured.subtitle && <p className="text-[var(--pg-ink)] mt-3">{featured.subtitle}</p>}
                  <p className="mt-5 text-xs font-mono tracking-[0.24em] pg-text-glow-gold">READ → {featured.read_time || ''}</p>
                </div>
              </Link>
            )}
            <div className="lg:col-span-5">
              <ul className="space-y-4">
                {rest.map((a, i) => (
                  <motion.li key={a.id} initial={{ opacity: 1, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: i * 0.04 }}>
                    <Link to={`/read/${a.slug}`} className="pg-card pg-card-hover block p-5">
                      <p className="pg-text-glow-gold font-mono text-[10px] tracking-[0.32em]">{(a.tags?.[0] || 'ESSAY').toUpperCase()}</p>
                      <h3 className="font-serif text-xl text-[var(--pg-ink)] mt-2">{a.title}</h3>
                      {a.excerpt && <p className="text-sm text-[var(--pg-ink)] mt-2 line-clamp-2">{a.excerpt}</p>}
                      <p className="mt-3 text-[11px] font-mono tracking-widest text-[var(--pg-ink-faint)]">{a.read_time || ''}</p>
                    </Link>
                  </motion.li>
                ))}
              </ul>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}

export function ArticleDetail() {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    setLoading(true);
    api.get(`/articles/${slug}`).then(({ data }) => { setArticle(data); setLoading(false); }).catch(() => setLoading(false));
  }, [slug]);

  useEffect(() => {
    const onScroll = () => {
      const h = document.documentElement.scrollHeight - window.innerHeight;
      setProgress(h > 0 ? Math.min(100, (window.scrollY / h) * 100) : 0);
    };
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  if (loading) return <div className="max-w-7xl mx-auto px-6 py-20 text-[var(--pg-ink-muted)]">Loading…</div>;
  if (!article) return <div className="max-w-7xl mx-auto px-6 py-20"><ChannelEmpty title="NOT FOUND" /></div>;

  return (
    <div>
      <div className="fixed top-0 left-0 right-0 h-[2px] z-50 bg-transparent">
        <div className="h-full" style={{ width: `${progress}%`, background: 'var(--pg-gold-2)', boxShadow: '0 0 14px rgba(226,199,122,0.5)', transition: 'width 120ms linear' }} />
      </div>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 pt-10 pb-6">
        <Link to="/read" className="inline-flex items-center gap-2 text-xs font-mono tracking-widest text-[var(--pg-ink-muted)] hover:text-[var(--pg-ink)] transition-colors">
          <ArrowLeft size={14} /> BACK TO READ
        </Link>
      </div>

      <article className="max-w-3xl mx-auto px-4 sm:px-6 pb-24">
        <div className="rounded-[var(--pg-radius-lg)] bg-[rgba(15,10,22,0.62)] backdrop-blur-md border border-[var(--pg-border-2)] px-6 sm:px-10 py-8 sm:py-12">
        <p data-testid="article-title-kicker" className="pg-text-glow-gold font-mono text-[11px] tracking-[0.32em]">{(article.tags?.[0] || 'OBSERVATION').toUpperCase()}</p>
        <h1 data-testid="article-title" className="font-serif text-4xl sm:text-5xl mt-3 tracking-[-0.02em] text-[var(--pg-ink)] leading-tight">{article.title}</h1>
        {article.subtitle && <p className="text-[var(--pg-ink)] mt-4 text-lg">{article.subtitle}</p>}
        <p className="mt-4 font-mono text-[11px] tracking-widest text-[var(--pg-ink-faint)]">{article.read_time || ''}{article.read_time && article.created_at ? ' · ' : ''}{article.created_at ? new Date(article.created_at).toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' }) : ''}</p>

        {article.cover_url && (
          <div className="mt-8 rounded-[var(--pg-radius-lg)] overflow-hidden border border-[var(--pg-border-2)]">
            <img src={mediaUrl(article.cover_url)} alt="" className="w-full h-auto object-cover" />
          </div>
        )}

        <div data-testid="article-body" className="pg-prose mt-10 text-base sm:text-lg leading-[1.85] max-w-[68ch]">
          {renderBody(article.body)}
        </div>
        </div>
      </article>
    </div>
  );
}
